import React, { useEffect } from 'react';
import { useQueries } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { TriangleAlert, ArrowUpRight } from 'lucide-react';
import { readPanel } from '../../lib/doctorCalls';
import { normPanel, followBackNote, settingsHref } from '../../lib/doctorView';
import { DOCTOR_TAG } from '../../lib/doctorSwitch';
import { panelQueryKey } from './DoctorDrawer';
import useChatsOwner from '../../hooks/useChatsOwner';

/*
 * r16sd SD22 (design 3.1, "follow-back page, P"): 92 "track follow-backs" is off on slots shown on the Follow-back page. The server
 * decides it per slot in the doctor panel; this note reads the panels of the slots whose follow-backs were not checked lately (the
 * only ones 92 OFF can explain), at most MAX of them, and lists those the server marks with SD22 in its own words, each with a link
 * to setting 92. Nothing is drawn when no panel carries SD22.
 */
const MAX = 6;

export default function FollowBackDoctorNote({ slots, enabled }) {
  const names = (Array.isArray(slots) ? slots : []).filter(Boolean).slice(0, MAX);
  const can = !!enabled;
  const owner = useChatsOwner();
  const results = useQueries({
    queries: names.map((s) => ({
      queryKey: panelQueryKey(owner, s),
      queryFn: () => readPanel(s).then((r) => r.data),
      enabled: can,
      staleTime: 5 * 60 * 1000,
      retry: 0,
    })),
  });
  const panels = results.map((r) => (r.data ? normPanel(r.data) : null)).filter(Boolean);
  const note = followBackNote(panels);
  const sig = note ? note.slots.join(',') : '';
  const done = results.every((r) => !r.isFetching);
  useEffect(() => {
    if (!can || !names.length || !done) return;
    // eslint-disable-next-line no-console
    console.info(`${DOCTOR_TAG} follow-back SD22: ${panels.length} of ${names.length} panels read, 92 off on ${sig || 'none'}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [can, done, sig]);
  if (!note) return null;
  return (
    <div className="flex items-start gap-3 rounded-2xl border border-warning/35 bg-warning/10 px-4 py-3" role="status" data-testid="fb-doctor-note">
      <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden />
      <div className="min-w-0 space-y-2">
        <p className="text-sm text-foreground text-pretty">{note.text}</p>
        <div className="flex flex-wrap gap-1.5">
          {note.slots.map((s) => (
            <Link key={s} to={settingsHref(s, 92)} data-testid={`fb-doctor-note-${s}`}
              className="tap inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-1 font-mono text-[11px] text-primary ring-1 ring-primary/25 hover:bg-primary/20">
              {s}{note.fix && note.fix.label ? <span className="font-sans">: {note.fix.label}</span> : null}
              <ArrowUpRight className="h-3 w-3" aria-hidden />
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
